import type {JSX} from 'react';
import {useState} from 'react';
import {toast} from 'react-toastify';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import Spinner from '@/assets/svg/Spinner';
import {Button, PermissionsProvider} from '@/components';
import {Input} from '@/components/common/Input';
import {merchantTransactionService} from '@/services/merchants/transactionService';
import {AfriexTransactionProcessor} from '@/types';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {BusinessTransactionChannel} from '@/types/AfriexBusinessTransactionChannel';
import {BusinessTransactionStatus} from '@/types/AfriexBusinessTransactionStatus';
import {BusinessTransactionType} from '@/types/AfriexBusinessTransactionType';
import {getResponseMessage, getThrownMessage} from '@/utils/errorMessage';

import {
  BusinessTransactionDetailTable,
  BusinessTransactionDetailTableHeader,
} from './TransactionDetail';

type TransactionSummaryProp = {
  data: AfriexBusinessTransaction;
  isMerchant?: boolean;
  // Receives the transaction as it stands after an admin action, so the row
  // can update in place instead of reloading the page (and losing the tab,
  // filters and page the admin was on).
  onUpdated?: (transaction: AfriexBusinessTransaction) => void;
};

export default function TransactionSummary({
  data,
  onUpdated,
}: TransactionSummaryProp): JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [uetrValue, setUetrValue] = useState<string>('');
  // Manual SWIFT is the only flow still managed from the admin; every other
  // refund or status change is handled outside it.
  const isManualSwift =
    data.destinationPaymentMethod.channel ===
      BusinessTransactionChannel.SWIFT &&
    data.meta?.processorMeta?.processorName ===
      AfriexTransactionProcessor.SYSTEM;
  const isRefundable: boolean =
    isManualSwift &&
    (data.status === BusinessTransactionStatus.FAILED ||
      data.status === BusinessTransactionStatus.PENDING ||
      data.status === BusinessTransactionStatus.PROCESSING) &&
    data.type === BusinessTransactionType.WITHDRAW;

  async function updateTransactionStatus(filters: {
    businessId: string;
    status: BusinessTransactionStatus;
    uetr?: string;
  }): Promise<void> {
    try {
      const message =
        filters.status === BusinessTransactionStatus.REFUNDED
          ? 'Requesting user wallet refund...'
          : 'Updating transaction status...';
      setLoadingMessage(message);
      setLoading(true);

      const res = await merchantTransactionService.updateBusinessTransaction(
        data.id,
        filters,
      );
      if (res) {
        setLoading(false);
        setLoadingMessage('');
        const message =
          filters.status === BusinessTransactionStatus.REFUNDED
            ? 'Refunded users wallet'
            : 'Updated Transaction to successful';
        toast.success(message);
        setUetrValue('');
        const updated = await merchantTransactionService
          .getBusinessTransaction({id: data.id})
          .catch(() => undefined);
        if (updated) {
          onUpdated?.(updated);
        }
      }
    } catch (err) {
      const errorMessage = getResponseMessage(err) || getThrownMessage(err);
      toast.error(errorMessage || 'Unknown Error. Contact Admin!');
      setLoading(false);
    }
  }

  const handleStatusUpdates = (status: BusinessTransactionStatus) =>
    updateTransactionStatus({
      businessId: data.businessId,
      status,
      ...(uetrValue.trim() && {uetr: uetrValue.trim()}),
    });

  // UETR is required for manual SWIFT — must be at least 3 chars
  const isUetrInvalid = isManualSwift && uetrValue.trim().length < 3;
  const isSuccessButtonDisabled = loading || isUetrInvalid;
  const isRefundButtonDisabled = loading;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg w-full px-5 sm:px-10 py-5 sm:py-10">
      <BusinessTransactionDetailTableHeader data={data} />
      <BusinessTransactionDetailTable data={data} />
      {loading && (
        <div className="w-full flex pt-5 sm:pt-10">
          <Spinner size={5} /> {loadingMessage}
        </div>
      )}
      <PermissionsProvider
        permission={AfriexAdminPermission.BusinessTransactionUpdate}
      >
        {isRefundable && (
          <>
            {isManualSwift && (
              <div className="pt-5 flex flex-col flex-start w-64 lg:w-72">
                <label className="text-sm font-medium mb-1">
                  UETR <span className="text-red-500">*</span>
                </label>
                <Input
                  name="uetr"
                  type="text"
                  value={uetrValue}
                  onChange={(e) => setUetrValue(e.target.value)}
                  placeholder="Enter UETR"
                  disabled={loading}
                  className="w-full"
                />
                {uetrValue.trim().length === 0 && (
                  <p className="text-red-500 text-xs mt-1">UETR is required</p>
                )}
                {uetrValue.trim().length > 0 && uetrValue.trim().length < 3 && (
                  <p className="text-red-500 text-xs mt-1">
                    UETR must be at least 3 characters
                  </p>
                )}
              </div>
            )}

            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full text-white pt-5 sm:pt-5"
              data-cy="merchant-transaction-status-actions"
            >
              <div>
                <Button
                  name="updateToSuccessful"
                  data-cy="merchant-transaction-update-successful"
                  disabled={isSuccessButtonDisabled}
                  onClick={() =>
                    handleStatusUpdates(BusinessTransactionStatus.SUCCESS)
                  }
                  variant="solid"
                  colorScheme="cyan"
                >
                  Update to Successful Transaction
                </Button>
              </div>
              {isRefundable && (
                <div>
                  <Button
                    name="refund"
                    data-cy="merchant-transaction-refund"
                    disabled={isRefundButtonDisabled}
                    onClick={() =>
                      handleStatusUpdates(BusinessTransactionStatus.REFUNDED)
                    }
                    variant="outline"
                    colorScheme="cyan"
                  >
                    Refund to Customer Wallet
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </PermissionsProvider>
    </div>
  );
}
